import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Save,
  Link as LinkIcon,
  ExternalLink,
  Loader2,
  CheckCircle,
  Globe,
  Video,
  BookOpen,
  Calendar,
  FileText,
  Settings,
  Plus,
  Trash2,
  GripVertical
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LinksManager = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [links, setLinks] = useState({
    webinar: {
      url: 'https://fengshuisignature.com/optin',
      text: 'Join the upcoming webinar',
      enabled: true
    },
    signatureOfWealth: {
      url: 'https://fengshuisignature.com/optin',
      text: 'Join the upcoming webinar',
      enabled: true
    },
    heroConsultation: {
      url: '/consultation-fees',
      text: 'Book Consultation',
      enabled: true
    },
    heroCourses: {
      url: '/courses',
      text: 'Explore Courses',
      enabled: true
    },
    navConsultation: {
      url: '/consultation-fees',
      text: 'Book Consultation',
      enabled: true
    },
    socialLinks: {
      facebook: { url: '', enabled: false },
      instagram: { url: '', enabled: false },
      youtube: { url: '', enabled: false },
      linkedin: { url: '', enabled: false },
      twitter: { url: '', enabled: false }
    },
    customLinks: []
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/site-links`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setLinks(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error('Error fetching links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/site-links`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(links)
      });
      
      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving links:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateLink = (category, field, value) => {
    setLinks(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const updateSocialLink = (platform, field, value) => {
    setLinks(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: {
          ...prev.socialLinks[platform],
          [field]: value
        }
      }
    }));
  };

  const addCustomLink = () => {
    setLinks(prev => ({
      ...prev,
      customLinks: [
        ...prev.customLinks,
        { id: Date.now(), name: '', url: '', location: 'footer', enabled: true }
      ]
    }));
  };

  const updateCustomLink = (id, field, value) => {
    setLinks(prev => ({
      ...prev,
      customLinks: prev.customLinks.map(link =>
        link.id === id ? { ...link, [field]: value } : link
      )
    }));
  };

  const removeCustomLink = (id) => {
    setLinks(prev => ({
      ...prev,
      customLinks: prev.customLinks.filter(link => link.id !== id)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Manage Links</h1>
              <p className="text-gray-400 text-sm">Update all links displayed on your homepage</p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : saved ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>

        {/* Webinar Link */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Hero Webinar Link</h2>
              <p className="text-gray-400 text-sm">The prominent webinar button in the hero section</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-gray-300">Enable Webinar Button</Label>
              <Switch
                checked={links.webinar.enabled}
                onCheckedChange={(checked) => updateLink('webinar', 'enabled', checked)}
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Button Text</Label>
              <Input
                value={links.webinar.text}
                onChange={(e) => updateLink('webinar', 'text', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Join the upcoming webinar"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Webinar URL</Label>
              <div className="flex gap-2">
                <Input
                  value={links.webinar.url}
                  onChange={(e) => updateLink('webinar', 'url', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                  placeholder="https://example.com/webinar"
                />
                <a
                  href={links.webinar.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Signature of Wealth Section */}
        <div className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 border border-amber-700/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Signature of Wealth</h2>
              <p className="text-gray-400 text-sm">The webinar button in the Signature of Wealth section</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-gray-300">Enable Section Button</Label>
              <Switch
                checked={links.signatureOfWealth?.enabled ?? true}
                onCheckedChange={(checked) => updateLink('signatureOfWealth', 'enabled', checked)}
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Button Text</Label>
              <Input
                value={links.signatureOfWealth?.text || 'Join the upcoming webinar'}
                onChange={(e) => updateLink('signatureOfWealth', 'text', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Join the upcoming webinar"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Button URL</Label>
              <div className="flex gap-2">
                <Input
                  value={links.signatureOfWealth?.url || 'https://fengshuisignature.com/optin'}
                  onChange={(e) => updateLink('signatureOfWealth', 'url', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                  placeholder="https://fengshuisignature.com/optin"
                />
                <a
                  href={links.signatureOfWealth?.url || 'https://fengshuisignature.com/optin'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Section Links */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Hero Section Links</h2>
              <p className="text-gray-400 text-sm">Main call-to-action buttons on the homepage hero</p>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Consultation Button */}
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-medium">Consultation Button</span>
                <Switch
                  checked={links.heroConsultation.enabled}
                  onCheckedChange={(checked) => updateLink('heroConsultation', 'enabled', checked)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm mb-1 block">Button Text</Label>
                  <Input
                    value={links.heroConsultation.text}
                    onChange={(e) => updateLink('heroConsultation', 'text', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm mb-1 block">URL</Label>
                  <Input
                    value={links.heroConsultation.url}
                    onChange={(e) => updateLink('heroConsultation', 'url', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Courses Button */}
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-medium">Courses Button</span>
                <Switch
                  checked={links.heroCourses.enabled}
                  onCheckedChange={(checked) => updateLink('heroCourses', 'enabled', checked)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm mb-1 block">Button Text</Label>
                  <Input
                    value={links.heroCourses.text}
                    onChange={(e) => updateLink('heroCourses', 'text', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm mb-1 block">URL</Label>
                  <Input
                    value={links.heroCourses.url}
                    onChange={(e) => updateLink('heroCourses', 'url', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Social Media Links</h2>
              <p className="text-gray-400 text-sm">Connect your social media profiles</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {Object.entries(links.socialLinks).map(([platform, data]) => (
              <div key={platform} className="flex items-center gap-4">
                <div className="w-24">
                  <span className="text-gray-300 capitalize">{platform}</span>
                </div>
                <Input
                  value={data.url}
                  onChange={(e) => updateSocialLink(platform, 'url', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                  placeholder={`https://${platform}.com/yourprofile`}
                />
                <Switch
                  checked={data.enabled}
                  onCheckedChange={(checked) => updateSocialLink(platform, 'enabled', checked)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Custom Links */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Custom Links</h2>
                <p className="text-gray-400 text-sm">Add additional links to display on your site</p>
              </div>
            </div>
            <Button
              onClick={addCustomLink}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Link
            </Button>
          </div>
          
          <div className="space-y-4">
            {links.customLinks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <LinkIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No custom links added yet</p>
              </div>
            ) : (
              links.customLinks.map((link) => (
                <div key={link.id} className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-lg">
                  <GripVertical className="w-5 h-5 text-gray-600" />
                  <Input
                    value={link.name}
                    onChange={(e) => updateCustomLink(link.id, 'name', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white w-40"
                    placeholder="Link Name"
                  />
                  <Input
                    value={link.url}
                    onChange={(e) => updateCustomLink(link.id, 'url', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white flex-1"
                    placeholder="https://example.com"
                  />
                  <select
                    value={link.location}
                    onChange={(e) => updateCustomLink(link.id, 'location', e.target.value)}
                    className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2"
                  >
                    <option value="footer">Footer</option>
                    <option value="navbar">Navbar</option>
                    <option value="hero">Hero</option>
                  </select>
                  <Switch
                    checked={link.enabled}
                    onCheckedChange={(checked) => updateCustomLink(link.id, 'enabled', checked)}
                  />
                  <button
                    onClick={() => removeCustomLink(link.id)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-6">
          <h3 className="text-white font-medium mb-3">Quick Reference - Current Links</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-400">
              <span>Hero Webinar:</span>
              <a href={links.webinar.url} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline truncate ml-4 max-w-md">
                {links.webinar.url}
              </a>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Signature of Wealth:</span>
              <a href={links.signatureOfWealth?.url} target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline truncate ml-4 max-w-md">
                {links.signatureOfWealth?.url || 'https://fengshuisignature.com/optin'}
              </a>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Consultation:</span>
              <span className="text-emerald-400">{links.heroConsultation.url}</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Courses:</span>
              <span className="text-cyan-400">{links.heroCourses.url}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinksManager;
